/* eslint-disable jsx-a11y/no-autofocus */
import { Box, Divider, Dropdown, FormField, ListItemSelect } from '@wix/design-system';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import type { TFunction } from './../../../../ricos-types/commonTypes';
import { useBiNext } from './../../../bi-context-provider/bi-context-provider';
import type { NamedLink } from './../../../link/types';
import { LINK_MODAL_ID } from './../../../link-modal/constants';
import { dataHook } from './../../../../wix-rich-content-common/index';

import { SECTION_SELECT, SECTION_TYPE_SELECT } from '../../data-hooks/data-hooks';
import type { DropdownOption } from '../types';
import { SectionEmptyState } from './empty-state';
import { type Section, useSectionOptions } from './use-options';

type Props = {
  link: NamedLink;
  onSelect: (anchor: string, defaultName: string | undefined) => void;
  t: TFunction;
};

const selectedListItem = 'selected-list-item';

export const SectionMobile: React.FC<Props> = ({ link, onSelect, t }) => {
  const logBi = useBiNext({ pluginId: 'link', modalId: LINK_MODAL_ID });
  const { sections, sectionTypeOptions } = useSectionOptions();

  const itemContainerRef = useRef<HTMLDivElement | undefined>();
  const [selectedTypeOption, setSelectedTypeOption] = useState<DropdownOption>(
    sectionTypeOptions[0]
  );

  const filteredSections = useMemo(
    () =>
      sections.filter(
        section => selectedTypeOption.id === 'all' || section.node.type === selectedTypeOption.id
      ),
    [sections, selectedTypeOption]
  );

  useEffect(() => {
    // setTimeout because of https://github.com/facebook/react/issues/23396
    setTimeout(() => {
      itemContainerRef.current
        ?.querySelector(dataHook(selectedListItem))
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);

  if (sections.length === 0) {
    return <SectionEmptyState t={t} />;
  }

  const onSelectType = (option: DropdownOption) => {
    setSelectedTypeOption(option);
    logBi({ dataHook: SECTION_TYPE_SELECT, value: `${option.id}` });
  };

  const onSelectSection = (section: Section) => {
    onSelect(section.id, section.title);
    logBi({ dataHook: SECTION_SELECT });
  };

  return (
    <>
      <Box direction="vertical" paddingLeft="SP3" paddingRight="SP3">
        <FormField label={t('LinkSettings_Sections_TypeDropdown_Label')} labelPlacement="top">
          <Dropdown
            dataHook={SECTION_TYPE_SELECT}
            onSelect={onSelectType}
            options={sectionTypeOptions}
            selectedId={selectedTypeOption.id}
            size="small"
            valueParser={option => option.label}
          />
        </FormField>
      </Box>
      <Divider />
      <Box
        direction="vertical"
        flexGrow={1}
        overflowY="auto"
        padding={0}
        paddingBottom="SP3"
        ref={itemContainerRef}
      >
        {filteredSections.map(section => {
          const selected = section.id === link.anchor;
          return (
            <ListItemSelect
              dataHook={selected ? selectedListItem : undefined}
              ellipsis
              titleMaxLines={2}
              highlighted={selected}
              key={section.id}
              onClick={() => onSelectSection(section)}
              prefix={section.prefixAvatar}
              selected={selected}
              size="medium"
              subtitle={section.subtitle}
              title={section.title}
            />
          );
        })}
      </Box>
    </>
  );
};
